import React, { useState, useEffect, useCallback } from 'react';
import { bridgeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRightLeft, ArrowUpRight, ArrowDownLeft, Loader2, CheckCircle, Clock, AlertCircle, RefreshCw, Copy, DollarSign, Building2, Landmark } from 'lucide-react';

const TRANSFER_TYPES = [
  { id: 'usd_route', label: 'USD → USDC', desc: 'Bank to Base Chain', icon: Landmark, color: 'text-emerald-400' },
  { id: 'onramp', label: 'Buy Crypto', desc: 'Fiat to Crypto', icon: ArrowUpRight, color: 'text-sky-400' },
  { id: 'offramp', label: 'Sell Crypto', desc: 'Crypto to Fiat', icon: ArrowDownLeft, color: 'text-amber-400' },
  { id: 'crypto', label: 'Swap', desc: 'Crypto to Crypto', icon: ArrowRightLeft, color: 'text-violet-400' },
];

const STATE_STYLES = {
  payment_processed: { color: 'text-emerald-400 bg-emerald-400/10', icon: CheckCircle, label: 'Completed' },
  awaiting_funds: { color: 'text-amber-400 bg-amber-400/10', icon: Clock, label: 'Awaiting Funds' },
  funds_sent: { color: 'text-sky-400 bg-sky-400/10', icon: Clock, label: 'Funds Sent' },
  funds_received: { color: 'text-sky-400 bg-sky-400/10', icon: Clock, label: 'Funds Received' },
  in_review: { color: 'text-amber-400 bg-amber-400/10', icon: Clock, label: 'In Review' },
  payment_submitted: { color: 'text-sky-400 bg-sky-400/10', icon: Clock, label: 'Payment Submitted' },
  pending: { color: 'text-zinc-400 bg-zinc-400/10', icon: Clock, label: 'Pending' },
  pending_kyc: { color: 'text-orange-400 bg-orange-400/10', icon: AlertCircle, label: 'KYC Pending' },
  canceled: { color: 'text-red-400 bg-red-400/10', icon: AlertCircle, label: 'Canceled' },
  failed: { color: 'text-red-400 bg-red-400/10', icon: AlertCircle, label: 'Failed' },
};

function StatusBadge({ state }) {
  const style = STATE_STYLES[state] || STATE_STYLES.pending;
  const Icon = style.icon;
  return (
    <span data-testid={`status-badge-${state}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.color}`}>
      <Icon className="w-3 h-3" />
      {style.label}
    </span>
  );
}

function CopyField({ label, value, mono = true }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-zinc-800/50 last:border-0">
      <div>
        <p className="text-xs text-zinc-500">{label}</p>
        <p className={`text-sm text-zinc-200 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
      <button data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`} onClick={handleCopy} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors">
        {copied ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function CryptoPayment() {
  const [supported, setSupported] = useState(null);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [paymentRoute, setPaymentRoute] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [routeTransfers, setRouteTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('usd_route');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const [usdForm, setUsdForm] = useState({ amount: '', source_rail: 'ach_push' });

  const [form, setForm] = useState({
    amount: '',
    source_currency: 'usd',
    source_rail: 'ach_push',
    destination_currency: 'usdc',
    destination_rail: 'ethereum',
    to_address: '',
    from_address: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const results = await Promise.allSettled([
        bridgeAPI.getStatus(),
        bridgeAPI.getSupported(),
        bridgeAPI.getTransfers(),
        bridgeAPI.getPaymentRoute(),
        bridgeAPI.getPaymentRouteTransfers(),
        bridgeAPI.getKycStatus(),
      ]);

      const [statusRes, supportedRes, transfersRes, routeRes, routeTransfersRes, kycRes] = results;

      if (statusRes.status === 'fulfilled') setBridgeStatus(statusRes.value.data);
      if (supportedRes.status === 'fulfilled') setSupported(supportedRes.value.data);
      if (transfersRes.status === 'fulfilled') setTransfers(transfersRes.value.data || []);
      if (routeRes.status === 'fulfilled') setPaymentRoute(routeRes.value.data);
      if (routeTransfersRes.status === 'fulfilled') setRouteTransfers(routeTransfersRes.value.data?.transfers || []);
      if (kycRes.status === 'fulfilled') setKycStatus(kycRes.value.data);

      // Show warning if some calls failed but not all
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0 && failed.length < results.length) {
        console.warn('Some Bridge API calls failed:', failed.map(f => f.reason?.message));
      } else if (failed.length === results.length) {
        setError('Failed to connect to Bridge — please check your connection and try again');
      }
    } catch {
      setError('Failed to load crypto data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // USD → USDC payment route transfer
  const handleUsdTransfer = async (e) => {
    e.preventDefault();
    if (!usdForm.amount || parseFloat(usdForm.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess(null);
    try {
      const res = await bridgeAPI.createPaymentRouteTransfer({
        amount: parseFloat(usdForm.amount),
        source_rail: usdForm.source_rail,
      });
      setSuccess(res.data);
      setUsdForm(f => ({ ...f, amount: '' }));
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  // General transfer
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess(null);
    try {
      const payload = {
        amount: parseFloat(form.amount),
        transfer_type: activeTab,
        source_currency: form.source_currency,
        source_rail: form.source_rail,
        destination_currency: form.destination_currency,
        destination_rail: form.destination_rail,
      };
      if (form.to_address) payload.to_address = form.to_address;
      if (form.from_address) payload.from_address = form.from_address;
      const res = await bridgeAPI.createTransfer(payload);
      setSuccess(res.data);
      setForm(f => ({ ...f, amount: '', to_address: '', from_address: '' }));
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getSourceOptions = () => activeTab === 'onramp' ? (supported?.fiat_rails || []) : (supported?.chains || []);
  const getDestOptions = () => activeTab === 'offramp' ? (supported?.fiat_rails || []) : (supported?.chains || []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid="crypto-payment-page" className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Crypto Transfers</h1>
          <p className="text-sm text-zinc-400 mt-1">Powered by Bridge.xyz</p>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="bridge-status-indicator" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${bridgeStatus?.status === 'available' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${bridgeStatus?.status === 'available' ? 'bg-emerald-400' : 'bg-red-400'}`} />
            {bridgeStatus?.status === 'available' ? 'Connected' : 'Offline'}
          </span>
          <Button data-testid="refresh-btn" variant="outline" size="sm" onClick={loadData} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KYC Verification Gate */}
      {kycStatus && !kycStatus.is_fully_verified && !kycStatus.management_bypass && (
        <Card data-testid="kyc-gate-banner" className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="py-5 px-6">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-amber-500/10 shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="space-y-2 flex-1">
                <h3 className="text-sm font-semibold text-amber-300">Verification Required</h3>
                <p className="text-xs text-zinc-400">Complete both verification steps to access crypto transfers.</p>
                <div className="flex items-center gap-6 mt-3">
                  <div className="flex items-center gap-2">
                    {kycStatus.internal_kyc_status === 'approved'
                      ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                      : <Clock className="w-4 h-4 text-amber-400" />}
                    <span className="text-xs text-zinc-300">Step 1: Internal KYC {kycStatus.internal_kyc_status === 'approved' ? '(Approved)' : '(Pending Review)'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {kycStatus.bridge_kyc_status === 'approved'
                      ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                      : <Clock className="w-4 h-4 text-amber-400" />}
                    <span className="text-xs text-zinc-300">Step 2: Bridge Verification {kycStatus.bridge_kyc_status === 'approved' ? '(Verified)' : '(Pending)'}</span>
                  </div>
                </div>
                {kycStatus.bridge_tos_link && kycStatus.internal_kyc_status === 'approved' && (
                  <a href={kycStatus.bridge_tos_link} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-md text-xs font-medium transition-colors"
                    data-testid="bridge-tos-link">
                    Complete Bridge Verification
                  </a>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transfer Type Tabs */}
      <div data-testid="transfer-type-tabs" className="flex gap-1 p-1 bg-zinc-900 rounded-lg border border-zinc-800">
        {TRANSFER_TYPES.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => { setActiveTab(t.id); setError(''); setSuccess(null); }}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-md text-sm font-medium transition-all ${activeTab === t.id ? 'bg-zinc-800 text-zinc-100 shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            <t.icon className={`w-4 h-4 ${activeTab === t.id ? t.color : ''}`} />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* USD → USDC Route Tab */}
      {activeTab === 'usd_route' && (
        <div className="grid gap-6 lg:grid-cols-5">
          {/* Transfer Form */}
          <Card data-testid="usd-route-form" className="lg:col-span-3 bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <CardTitle className="text-lg text-zinc-100">USD → USDC (Base)</CardTitle>
                  <CardDescription className="text-zinc-500">
                    On-ramp via virtual account &middot; {paymentRoute?.developer_fee || '1%'} fee
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUsdTransfer} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">Amount (USD)</label>
                  <Input
                    data-testid="usd-amount-input"
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder="100.00"
                    value={usdForm.amount}
                    onChange={e => setUsdForm(f => ({ ...f, amount: e.target.value }))}
                    className="bg-zinc-800/50 border-zinc-700 text-zinc-100 text-lg h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">Payment Method</label>
                  <select
                    data-testid="usd-source-rail"
                    value={usdForm.source_rail}
                    onChange={e => setUsdForm(f => ({ ...f, source_rail: e.target.value }))}
                    className="w-full bg-zinc-800/50 border border-zinc-700 text-zinc-100 rounded-md px-3 py-2.5 text-sm"
                  >
                    <option value="ach_push">ACH Push</option>
                    <option value="wire">Wire Transfer</option>
                  </select>
                </div>

                <div className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-800 text-xs text-zinc-400 space-y-1">
                  <p className="flex items-center gap-1.5 text-zinc-300 font-medium"><Building2 className="w-3.5 h-3.5" /> Destination</p>
                  <p>Chain: <span className="text-zinc-200">Base</span></p>
                  <p>Currency: <span className="text-zinc-200">USDC</span></p>
                  <p className="truncate">Wallet: <span className="text-zinc-200 font-mono text-[11px]">{paymentRoute?.destination?.wallet_address}</span></p>
                </div>

                {error && (
                  <div data-testid="transfer-error" className="flex items-center gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                {success && (
                  <div data-testid="transfer-success" className="p-4 rounded-md bg-emerald-500/10 border border-emerald-500/20 space-y-3">
                    <div className="flex items-center gap-2 text-emerald-400 font-medium text-sm">
                      <CheckCircle className="w-4 h-4" />
                      Transfer Created — {success.bridge_kyc_required ? 'Complete Bridge KYC to process' : 'Send deposit to complete'}
                    </div>
                    {success.bridge_kyc_required && success.bridge_tos_link && (
                      <div className="p-2.5 rounded bg-orange-500/10 border border-orange-500/20 text-xs text-orange-300">
                        <p className="font-medium mb-1">Bridge KYC Required</p>
                        <p className="text-orange-400/80 mb-2">{success.message}</p>
                        <a href={success.bridge_tos_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-orange-300 hover:text-orange-200 underline">
                          Complete Bridge Verification
                        </a>
                      </div>
                    )}
                    {success.source_deposit_instructions && (
                      <div className="space-y-0">
                        <CopyField label="Routing Number" value={success.source_deposit_instructions.bank_routing_number} />
                        <CopyField label="Account Number" value={success.source_deposit_instructions.bank_account_number} />
                        <CopyField label="Beneficiary" value={success.source_deposit_instructions.beneficiary_name} mono={false} />
                        {success.source_deposit_instructions.deposit_message && (
                          <CopyField label="Memo / Reference (Required)" value={success.source_deposit_instructions.deposit_message} />
                        )}
                        <CopyField label="Amount" value={`$${success.amount} USD`} mono={false} />
                      </div>
                    )}
                  </div>
                )}

                <Button
                  data-testid="usd-submit-btn"
                  type="submit"
                  disabled={submitting || bridgeStatus?.status !== 'available'}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
                  {submitting ? 'Processing...' : 'Create USD → USDC Transfer'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Bank Details Panel */}
          <div className="lg:col-span-2 space-y-4">
            <Card data-testid="bank-details-card" className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                  <Landmark className="w-4 h-4" /> Bank Deposit Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                <CopyField label="Bank Routing Number" value={paymentRoute?.bank_details?.bank_routing_number || '101019644'} />
                <CopyField label="Bank Account Number" value={paymentRoute?.bank_details?.bank_account_number || '214088326179'} />
                <CopyField label="Beneficiary Name" value={paymentRoute?.bank_details?.beneficiary_name || 'Bullaale AS'} mono={false} />
                <CopyField label="Beneficiary Address" value={paymentRoute?.bank_details?.beneficiary_address || ''} mono={false} />
                <CopyField label="Currency" value="USD" mono={false} />
                <CopyField label="Payment Rails" value="ACH, Wire" mono={false} />
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-zinc-400">Route Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Route</span><span className="text-zinc-200">{paymentRoute?.name}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Status</span><span className="text-emerald-400 capitalize">{paymentRoute?.status}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Fee</span><span className="text-zinc-200">{paymentRoute?.developer_fee}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Dest Chain</span><span className="text-zinc-200">Base</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Dest Token</span><span className="text-zinc-200">USDC</span></div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* General Transfer Tabs (Buy, Sell, Swap) */}
      {activeTab !== 'usd_route' && (
        <div className="grid gap-8 lg:grid-cols-5">
          <Card data-testid="transfer-form-card" className="lg:col-span-3 bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-zinc-100">
                {TRANSFER_TYPES.find(t => t.id === activeTab)?.label}
              </CardTitle>
              <CardDescription className="text-zinc-500">
                {TRANSFER_TYPES.find(t => t.id === activeTab)?.desc}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1.5">Amount</label>
                  <Input
                    data-testid="transfer-amount-input"
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="bg-zinc-800/50 border-zinc-700 text-zinc-100 text-lg h-12"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1.5">
                      {activeTab === 'onramp' ? 'Pay With' : 'From Chain'}
                    </label>
                    <select
                      data-testid="source-rail-select"
                      value={form.source_rail}
                      onChange={e => {
                        const rail = getSourceOptions().find(r => r.id === e.target.value);
                        setForm(f => ({ ...f, source_rail: e.target.value, source_currency: rail?.currencies[0] || f.source_currency }));
                      }}
                      className="w-full bg-zinc-800/50 border border-zinc-700 text-zinc-100 rounded-md px-3 py-2.5 text-sm"
                    >
                      {getSourceOptions().map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1.5">Currency</label>
                    <select
                      data-testid="source-currency-select"
                      value={form.source_currency}
                      onChange={e => setForm(f => ({ ...f, source_currency: e.target.value }))}
                      className="w-full bg-zinc-800/50 border border-zinc-700 text-zinc-100 rounded-md px-3 py-2.5 text-sm"
                    >
                      {(getSourceOptions().find(r => r.id === form.source_rail)?.currencies || []).map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1.5">
                      {activeTab === 'offramp' ? 'Receive Via' : 'To Chain'}
                    </label>
                    <select
                      data-testid="dest-rail-select"
                      value={form.destination_rail}
                      onChange={e => {
                        const rail = getDestOptions().find(r => r.id === e.target.value);
                        setForm(f => ({ ...f, destination_rail: e.target.value, destination_currency: rail?.currencies[0] || f.destination_currency }));
                      }}
                      className="w-full bg-zinc-800/50 border border-zinc-700 text-zinc-100 rounded-md px-3 py-2.5 text-sm"
                    >
                      {getDestOptions().map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1.5">Currency</label>
                    <select
                      data-testid="dest-currency-select"
                      value={form.destination_currency}
                      onChange={e => setForm(f => ({ ...f, destination_currency: e.target.value }))}
                      className="w-full bg-zinc-800/50 border border-zinc-700 text-zinc-100 rounded-md px-3 py-2.5 text-sm"
                    >
                      {(getDestOptions().find(r => r.id === form.destination_rail)?.currencies || []).map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                    </select>
                  </div>
                </div>
                {(activeTab === 'onramp' || activeTab === 'crypto') && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1.5">Destination Wallet Address (optional)</label>
                    <Input data-testid="to-address-input" placeholder="0x..." value={form.to_address} onChange={e => setForm(f => ({ ...f, to_address: e.target.value }))} className="bg-zinc-800/50 border-zinc-700 text-zinc-100" />
                  </div>
                )}
                {(activeTab === 'offramp' || activeTab === 'crypto') && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1.5">Source Wallet Address (optional)</label>
                    <Input data-testid="from-address-input" placeholder="0x..." value={form.from_address} onChange={e => setForm(f => ({ ...f, from_address: e.target.value }))} className="bg-zinc-800/50 border-zinc-700 text-zinc-100" />
                  </div>
                )}
                {error && (
                  <div data-testid="transfer-error" className="flex items-center gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 shrink-0" />{error}
                  </div>
                )}
                {success && (
                  <div data-testid="transfer-success" className="p-4 rounded-md bg-emerald-500/10 border border-emerald-500/20 space-y-2">
                    <div className="flex items-center gap-2 text-emerald-400 font-medium text-sm"><CheckCircle className="w-4 h-4" /> Transfer Created Successfully</div>
                    {success.source_deposit_instructions && (
                      <div className="text-xs text-zinc-400 space-y-1 mt-2">
                        <p className="font-medium text-zinc-300">Deposit Instructions:</p>
                        {success.source_deposit_instructions.amount && <p>Amount: <span className="text-zinc-200">{success.source_deposit_instructions.amount} {success.source_deposit_instructions.currency?.toUpperCase()}</span></p>}
                        {success.source_deposit_instructions.deposit_message && (
                          <CopyField label="Memo" value={success.source_deposit_instructions.deposit_message} />
                        )}
                      </div>
                    )}
                  </div>
                )}
                <Button data-testid="submit-transfer-btn" type="submit" disabled={submitting || bridgeStatus?.status !== 'available'} className="w-full h-11 bg-zinc-100 text-zinc-900 hover:bg-zinc-200 font-medium">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {submitting ? 'Processing...' : `Create ${TRANSFER_TYPES.find(t => t.id === activeTab)?.label} Transfer`}
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3"><CardTitle className="text-sm text-zinc-400">Supported Chains</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {supported?.chains?.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-zinc-200">{c.name}</span>
                    <span className="text-zinc-500 text-xs">{c.currencies.map(cu => cu.toUpperCase()).join(', ')}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3"><CardTitle className="text-sm text-zinc-400">Payment Rails</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {supported?.fiat_rails?.map(r => (
                  <div key={r.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-zinc-200">{r.name}</span>
                    <span className="text-zinc-500 text-xs">{r.currencies.map(c => c.toUpperCase()).join(', ')}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Transfer History */}
      <Card data-testid="transfer-history-card" className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg text-zinc-100">
            {activeTab === 'usd_route' ? 'USD → USDC Transfer History' : 'Transfer History'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const displayTransfers = activeTab === 'usd_route' ? routeTransfers : transfers;
            if (displayTransfers.length === 0) {
              return <p data-testid="no-transfers" className="text-sm text-zinc-500 text-center py-8">No transfers yet</p>;
            }
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-2 text-zinc-500 font-medium">Type</th>
                      <th className="text-left py-3 px-2 text-zinc-500 font-medium">Amount</th>
                      <th className="text-left py-3 px-2 text-zinc-500 font-medium">Route</th>
                      <th className="text-left py-3 px-2 text-zinc-500 font-medium">Status</th>
                      <th className="text-left py-3 px-2 text-zinc-500 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayTransfers.map(t => (
                      <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <td className="py-3 px-2 capitalize text-zinc-200">{t.transfer_type}</td>
                        <td className="py-3 px-2 text-zinc-200 font-mono">${t.amount?.toFixed(2)}</td>
                        <td className="py-3 px-2 text-zinc-400 text-xs">{t.source_currency?.toUpperCase()} ({t.source_rail}) → {t.destination_currency?.toUpperCase()} ({t.destination_rail})</td>
                        <td className="py-3 px-2"><StatusBadge state={t.state} /></td>
                        <td className="py-3 px-2 text-zinc-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
